import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

/**
 * The unpublished job drafts this account has, wherever the panel shows them
 * (N9 projection two, N36).
 *
 * **One component because there is more than one screen, and the screens must
 * not drift.** N9(b) put this on the overview alone, and the overview is not
 * where a person looks for their jobs: a client with a live draft opened "Mis
 * trabajos", was told "Aún sin trabajos", and the work Dany had written with
 * them was reachable only by remembering which conversation it was in — which
 * is precisely what the spec says must not be required. Production
 * 2026-09-19: `draft_bf42716340ec`, "Limpieza de patio", `status: ready`, a
 * live card in its own thread and nothing anywhere in the panel.
 *
 * Still a read of the same row the card is drawn from — `GET /dany/drafts`,
 * not a copy — for the reason the endpoint's own docstring gives: a dashboard
 * that kept its own record of what was being written would be a second thing
 * to keep true, and the first time the two disagreed somebody would be looking
 * at a draft that no longer exists.
 *
 * A failure is silence. An unpublished draft is a helpful reminder, not a fact
 * this page owes anybody, and a red box where a reminder would be is worse
 * than no reminder.
 *
 * Role-agnostic, like the endpoint: since DY2.6 a FixyBee posts jobs too, and
 * "Continuar con Dany" goes to the conversation either way — the draft has no
 * screen of its own on purpose, because continuing means going back to where
 * the card and Dany both already are.
 */
export default function DanyDraftList({ className = "" }) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    let live = true;
    api.get("/dany/drafts")
      .then((r) => { if (live) setDrafts(r.data.drafts || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  if (!drafts.length) return null;

  return (
    <div className={`space-y-2 ${className}`} data-testid="client-drafts">
      {drafts.map((d) => (
        <div key={d.draft_id}
             className="card-base p-4 flex items-center justify-between gap-3 flex-wrap"
             data-testid={`draft-${d.draft_id}`}>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-smoke">
              {t("dashboard.client.draftLabel")}
            </p>
            <p className="font-display font-bold text-worker truncate">
              {d.title || t("dashboard.client.draftUntitled")}
            </p>
            <p className="text-sm text-smoke">
              {(d.missing || []).length
                ? t("dashboard.client.draftMissing", {
                    // The same keys the card's own legend uses, so the panel
                    // and the card name a missing field with one word between
                    // them (N27).
                    fields: d.missing
                      .map((f) => t(`dany.job.field${f.charAt(0).toUpperCase()}${f.slice(1)}`, f))
                      .join(", "),
                  })
                : t("dashboard.client.draftReady")}
            </p>
          </div>
          <Link to={`/dany/${d.thread_id}`} className="fx-btn fx-btn--secondary shrink-0"
                data-testid={`draft-continue-${d.draft_id}`}>
            {t("dashboard.client.draftContinue")}
          </Link>
        </div>
      ))}
    </div>
  );
}
